import React from "react";
import { motion } from "framer-motion";

interface MascotProps {
  expression?: "reading" | "happy";
  className?: string;
  size?: number;
}

export const Mascot: React.FC<MascotProps> = ({
  expression = "reading",
  className = "",
  size = 200,
}) => {
  if (expression === "reading") {
    return (
      <motion.div
        className={`relative select-none ${className}`}
        style={{ width: size, height: size * 1.25 }}
        animate={{ y: [-6, 6, -6] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      >
        <svg
          viewBox="0 0 200 250"
          className="w-full h-full drop-shadow-2xl"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Defs for gradients */}
          <defs>
            <linearGradient id="moonGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#f8fafc" />
              <stop offset="60%" stopColor="#e2e8f0" />
              <stop offset="100%" stopColor="#cbd5e1" />
            </linearGradient>
            <linearGradient id="owlBlue" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#2563eb" />
              <stop offset="50%" stopColor="#1d4ed8" />
              <stop offset="100%" stopColor="#1e3a8a" />
            </linearGradient>
            <linearGradient id="hoodieNavy" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#334155" />
              <stop offset="100%" stopColor="#0f172a" />
            </linearGradient>
            <linearGradient id="beakGold" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#fbbf24" />
              <stop offset="100%" stopColor="#d97706" />
            </linearGradient>
            <radialGradient id="eyeYellow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#fef08a" />
              <stop offset="70%" stopColor="#fbbf24" />
              <stop offset="100%" stopColor="#ca8a04" />
            </radialGradient>
            <filter id="shadow" x="-10%" y="-10%" width="120%" height="120%">
              <feDropShadow dx="0" dy="4" stdDeviation="4" floodOpacity="0.15" />
            </filter>
          </defs>

          {/* Crescent Moon */}
          <g filter="url(#shadow)">
            <path
              d="M 50,225 C 130,225 180,165 180,95 C 180,60 165,30 145,15 C 160,35 165,70 155,105 C 142,150 100,185 50,185 C 35,185 20,180 10,172 C 20,205 32,225 50,225 Z"
              fill="url(#moonGrad)"
            />
            {/* Moon textures/craters */}
            <circle cx="115" cy="155" r="5" fill="#94a3b8" opacity="0.25" />
            <circle cx="135" cy="125" r="8" fill="#94a3b8" opacity="0.2" />
            <circle cx="150" cy="90" r="4" fill="#94a3b8" opacity="0.3" />
            <path d="M 125,175 C 135,175 140,180 135,185" stroke="#94a3b8" strokeWidth="2" opacity="0.2" strokeLinecap="round" />
          </g>

          {/* Owl sitting on moon */}
          <g transform="translate(45, 55)">
            {/* Legs & Shoes */}
            <rect x="32" y="115" width="10" height="15" rx="3" fill="#0f172a" />
            <rect x="48" y="115" width="10" height="15" rx="3" fill="#0f172a" />
            
            {/* Left Shoe */}
            <path d="M 26,128 L 42,128 C 42,122 36,122 30,122 L 26,125 Z" fill="#2563eb" />
            <path d="M 26,128 L 42,128 L 42,131 L 26,131 Z" fill="#ffffff" /> {/* Sole */}
            
            {/* Right Shoe */}
            <path d="M 48,128 L 64,128 C 64,122 58,122 52,122 L 48,125 Z" fill="#2563eb" />
            <path d="M 48,128 L 64,128 L 64,131 L 48,131 Z" fill="#ffffff" /> {/* Sole */}

            {/* Pants & Body */}
            <path d="M 20,80 C 20,120 70,120 70,80 Z" fill="url(#hoodieNavy)" />
            <path d="M 28,100 L 38,120 L 44,120 L 44,100 Z" fill="#0f172a" opacity="0.2" /> {/* Shadow between legs */}

            {/* Hoodie Body */}
            <rect x="18" y="45" width="54" height="45" rx="15" fill="url(#hoodieNavy)" />
            
            {/* Hoodie Pocket */}
            <path d="M 26,75 L 64,75 L 58,60 L 32,60 Z" fill="#1e293b" />
            
            {/* Head (Owl) */}
            <path d="M 15,25 C 15,4 30,8 45,15 C 60,8 75,4 75,25 C 75,55 15,55 15,25 Z" fill="url(#owlBlue)" />

            {/* Owl ears details / horn-like tufts */}
            <path d="M 15,15 L 25,2 C 25,2 20,15 22,20 Z" fill="#1e3a8a" />
            <path d="M 75,15 L 65,2 C 65,2 70,15 68,20 Z" fill="#1e3a8a" />

            {/* White face rings around eyes */}
            <circle cx="34" cy="32" r="14" fill="#f8fafc" />
            <circle cx="56" cy="32" r="14" fill="#f8fafc" />

            {/* Eye Yellow/Iris */}
            <circle cx="34" cy="32" r="10" fill="url(#eyeYellow)" />
            <circle cx="56" cy="32" r="10" fill="url(#eyeYellow)" />

            {/* Pupils looking down/at newspaper */}
            <circle cx="36" cy="34" r="5" fill="#0f172a" />
            <circle cx="54" cy="34" r="5" fill="#0f172a" />

            {/* Pupil Highlights */}
            <circle cx="38" cy="32" r="1.5" fill="#ffffff" />
            <circle cx="56" cy="32" r="1.5" fill="#ffffff" />

            {/* Glasses (Tortoise Spectacles) */}
            <circle cx="34" cy="32" r="13" stroke="#7c2d12" strokeWidth="2.5" />
            <circle cx="56" cy="32" r="13" stroke="#7c2d12" strokeWidth="2.5" />
            <line x1="47" y1="32" x2="43" y2="32" stroke="#7c2d12" strokeWidth="3" />
            {/* Frame extensions */}
            <path d="M 21,32 C 17,28 12,28 12,28" stroke="#7c2d12" strokeWidth="2" strokeLinecap="round" />
            <path d="M 69,32 C 73,28 78,28 78,28" stroke="#7c2d12" strokeWidth="2" strokeLinecap="round" />

            {/* Beak */}
            <polygon points="45,34 41,44 49,44" fill="url(#beakGold)" />

            {/* Hoodie Hood fold (around neck) */}
            <path d="M 20,46 C 25,38 65,38 70,46 C 72,54 18,54 20,46 Z" fill="#1e293b" stroke="#0f172a" strokeWidth="1.5" />
            {/* Drawstrings */}
            <line x1="41" y1="50" x2="38" y2="65" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" />
            <line x1="49" y1="50" x2="52" y2="68" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="38" cy="65" r="1.5" fill="#fbbf24" />
            <circle cx="52" cy="68" r="1.5" fill="#fbbf24" />

            {/* Newspaper (Held in wings) */}
            <g transform="translate(16, 52)">
              {/* Back shadows/folds of paper */}
              <polygon points="2,38 56,22 58,45 8,55" fill="#cbd5e1" />
              {/* Main Newspaper Paper block */}
              <polygon points="0,35 55,18 60,40 10,50" fill="#f8fafc" stroke="#94a3b8" strokeWidth="1" />
              
              {/* "Literally Times" Title */}
              <text x="6" y="27" fill="#0f172a" fontSize="3" fontWeight="bold" fontFamily="Georgia, serif" transform="rotate(-17 10 30)">
                Literally Times
              </text>
              {/* Sub-header text */}
              <text x="5" y="31" fill="#475569" fontSize="1.5" fontFamily="sans-serif" transform="rotate(-17 10 30)">
                Words. Ideas. Impact.
              </text>
              <line x1="6" y1="33" x2="32" y2="25" stroke="#94a3b8" strokeWidth="0.5" transform="rotate(-17 10 30)" />
              
              {/* Large Bold "L." */}
              <rect x="36" y="24" width="10" height="10" fill="#0f172a" transform="rotate(-17 10 30)" />
              <text x="39" y="32" fill="#ffffff" fontSize="8" fontWeight="black" fontFamily="Georgia, serif" transform="rotate(-17 10 30)">
                L
              </text>
              <circle cx="43" cy="31" r="1" fill="#38bdf8" transform="rotate(-17 10 30)" />

              {/* Mock columns text lines */}
              <line x1="6" y1="36" x2="24" y2="30.5" stroke="#64748b" strokeWidth="0.5" transform="rotate(-17 10 30)" />
              <line x1="6" y1="38" x2="28" y2="31" stroke="#64748b" strokeWidth="0.5" transform="rotate(-17 10 30)" />
              <line x1="6" y1="40" x2="20" y2="35.5" stroke="#64748b" strokeWidth="0.5" transform="rotate(-17 10 30)" />
              
              <line x1="6" y1="43" x2="44" y2="31" stroke="#64748b" strokeWidth="0.5" transform="rotate(-17 10 30)" />
              <line x1="6" y1="45" x2="44" y2="33" stroke="#64748b" strokeWidth="0.5" transform="rotate(-17 10 30)" />
            </g>

            {/* Wings / Arms holding the paper */}
            {/* Left wing */}
            <path d="M 18,52 C 12,56 12,68 22,68 C 24,68 24,56 20,52 Z" fill="url(#owlBlue)" stroke="#0f172a" strokeWidth="1" />
            {/* Right wing */}
            <path d="M 72,52 C 78,56 78,68 68,68 C 66,68 66,56 70,52 Z" fill="url(#owlBlue)" stroke="#0f172a" strokeWidth="1" />
          </g>
        </svg>
      </motion.div>
    );
  } else {
    // Happy/Waving variant
    return (
      <motion.div
        className={`relative select-none ${className}`}
        style={{ width: size, height: size * 1.1 }}
      >
        <svg
          viewBox="0 0 200 220"
          className="w-full h-full drop-shadow-2xl"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id="owlBlue" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#2563eb" />
              <stop offset="50%" stopColor="#1d4ed8" />
              <stop offset="100%" stopColor="#1e3a8a" />
            </linearGradient>
            <linearGradient id="hoodieNavy" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#334155" />
              <stop offset="100%" stopColor="#0f172a" />
            </linearGradient>
            <linearGradient id="beakGold" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#fbbf24" />
              <stop offset="100%" stopColor="#d97706" />
            </linearGradient>
            <radialGradient id="eyeYellow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#fef08a" />
              <stop offset="70%" stopColor="#fbbf24" />
              <stop offset="100%" stopColor="#ca8a04" />
            </radialGradient>
            <filter id="shadow" x="-10%" y="-10%" width="120%" height="120%">
              <feDropShadow dx="0" dy="4" stdDeviation="4" floodOpacity="0.15" />
            </filter>
          </defs>

          {/* Body & Clothes */}
          <g transform="translate(50, 40)">
            {/* Waving Arm (Left wing raised in joy) */}
            <motion.path
              d="M 18,48 C 6,40 -2,15 6,5 C 12,2 18,15 20,38 Z"
              fill="url(#owlBlue)"
              stroke="#0f172a"
              strokeWidth="1"
              animate={{ rotate: [-8, 12, -8] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              style={{ originX: "18px", originY: "42px" }}
            />

            {/* Other Arm (Right wing resting comfortably behind) */}
            <path d="M 82,48 C 94,40 102,15 94,5 C 88,2 82,15 80,38 Z" fill="url(#owlBlue)" stroke="#0f172a" strokeWidth="1" />

            {/* Happy Jumping Legs & Shoes */}
            {/* Left bent leg (happy pose) */}
            <motion.g
              animate={{ y: [0, -4, 0] }}
              transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}
            >
              <rect x="30" y="115" width="12" height="15" rx="3" fill="#0f172a" transform="rotate(10 30 115)" />
              <path d="M 22,126 L 38,126 C 38,120 32,120 26,120 L 22,123 Z" fill="#2563eb" transform="rotate(10 30 115)" />
              <path d="M 22,126 L 38,126 L 38,129 L 22,129 Z" fill="#ffffff" transform="rotate(10 30 115)" />
            </motion.g>

            {/* Right straight leg */}
            <rect x="58" y="115" width="12" height="15" rx="3" fill="#0f172a" />
            <path d="M 58,128 L 74,128 C 74,122 68,122 62,122 L 58,125 Z" fill="#2563eb" />
            <path d="M 58,128 L 74,128 L 74,131 L 58,131 Z" fill="#ffffff" />

            {/* Pants & Body */}
            <path d="M 20,80 C 20,122 80,122 80,80 Z" fill="url(#hoodieNavy)" />

            {/* Hoodie Body */}
            <rect x="18" y="45" width="64" height="45" rx="15" fill="url(#hoodieNavy)" />
            
            {/* Hoodie Pocket */}
            <path d="M 26,75 L 74,75 L 68,60 L 32,60 Z" fill="#1e293b" />

            {/* Head (Owl) */}
            <path d="M 15,25 C 15,4 35,8 50,15 C 65,8 85,4 85,25 C 85,55 15,55 15,25 Z" fill="url(#owlBlue)" />

            {/* Owl ears details */}
            <path d="M 15,15 L 25,2 C 25,2 20,15 22,20 Z" fill="#1e3a8a" />
            <path d="M 85,15 L 75,2 C 75,2 80,15 78,20 Z" fill="#1e3a8a" />

            {/* White face rings */}
            <circle cx="36" cy="32" r="14" fill="#f8fafc" />
            <circle cx="64" cy="32" r="14" fill="#f8fafc" />

            {/* Eye Yellow/Iris */}
            <circle cx="36" cy="32" r="10" fill="url(#eyeYellow)" />
            <circle cx="64" cy="32" r="10" fill="url(#eyeYellow)" />

            {/* Pupils looking straight at user excitedly! */}
            <circle cx="36" cy="32" r="5.5" fill="#0f172a" />
            <circle cx="64" cy="32" r="5.5" fill="#0f172a" />

            {/* Highlighting eyes for extra excitement */}
            <circle cx="38.5" cy="29.5" r="2" fill="#ffffff" />
            <circle cx="66.5" cy="29.5" r="2" fill="#ffffff" />
            <circle cx="34.5" cy="34" r="1" fill="#ffffff" />
            <circle cx="62.5" cy="34" r="1" fill="#ffffff" />

            {/* Glasses (Tortoise Spectacles) */}
            <circle cx="36" cy="32" r="13" stroke="#7c2d12" strokeWidth="2.5" />
            <circle cx="64" cy="32" r="13" stroke="#7c2d12" strokeWidth="2.5" />
            <line x1="49" y1="32" x2="51" y2="32" stroke="#7c2d12" strokeWidth="3" />
            <path d="M 23,32 C 19,28 14,28 14,28" stroke="#7c2d12" strokeWidth="2" strokeLinecap="round" />
            <path d="M 77,32 C 81,28 86,28 86,28" stroke="#7c2d12" strokeWidth="2" strokeLinecap="round" />

            {/* Beak */}
            <polygon points="50,34 46,44 54,44" fill="url(#beakGold)" />

            {/* Hoodie Hood fold */}
            <path d="M 20,46 C 25,38 75,38 80,46 C 82,54 18,54 20,46 Z" fill="#1e293b" stroke="#0f172a" strokeWidth="1.5" />
            {/* Drawstrings */}
            <line x1="45" y1="50" x2="42" y2="65" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" />
            <line x1="55" y1="50" x2="58" y2="68" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="42" cy="65" r="1.5" fill="#fbbf24" />
            <circle cx="58" cy="68" r="1.5" fill="#fbbf24" />
          </g>
        </svg>
      </motion.div>
    );
  }
};
